import type { WritableDraft } from "immer";
import {
  CHART_TYPES_BY_LABEL,
  ChartTypeName,
  getInitialReportConfig,
  ReportConfigV2,
} from "./report-configuration.ts";

import { MeasureName, MEASURES } from "./measures.ts";
import {
  addTimeDimensionByDataset,
  DimensionName,
  DIMENSIONS,
  findTimeDimensionV2,
} from "./dimensions.ts";
import {
  addTimeFilterByDataset,
  DateFilterDefinition,
  FilterName,
  FILTERS,
  findTimeFilterV2,
} from "./filters.ts";
import { Temporal } from "temporal-polyfill";
import {
  parsePlainDate,
  todayUTC,
  toISODate,
  toPlainDate,
} from "./dateUtils.ts";

/**
 * This validator exists to centralize logic for the ReportConfigV2 schema. Changes
 * to the configuration happen incrementally in the report designer, but all at once
 * when generated by the AI. The AI generation path was using the previous version of the
 * reportReducer to incrementally update the config, which gave rise to the possiblity of
 * validation errors caused by the need to traverse an invalid intermediate state.
 *
 * This validator can be used in both contexts to ensure that the config is valid. Incremental
 * updates from the report designer should generate at most one validation error at a time,
 * while the AI generation path may generate multiple errors at once. In case of validation
 * failures during AI generation, the repair flag can be set to true to have the validator
 * fix things automatically. Conversely, we may take different actions when a report
 * is being modified by the user, so the reducer does not set the repair flag to true.
 */

export const REPORT_CONFIG_ERROR_CODES = {
  ANSWER_VALUE_DIMENSION_NOT_ALLOWED_FOR_AVG_SURVEY_SCORE: 1,
  ANSWER_VALUE_DIMENSION_ONLY_VALID_WITH_ONE_OTHER_DIMENSION: 2,
  ANSWER_VALUE_DIMENSION_MUST_COME_FIRST: 3,
  AVG_SURVEY_SCORE_NOT_ALLOWED_WITH_SURVEY_SCORE: 4,
  ATTRITION_DRIVERS_DOES_NOT_SUPPORT_DATE_DIMENSION: 5,
  ATTRITION_DRIVERS_FORCES_SPECIAL_CONFIGURATION: 6,
  ATTRITION_PROBABILITY_REQUIRES_ATTRITION_TIME_HORIZON_FILTER: 7,
  ATTRITION_PROBABILITY_TIME_HORIZON_ACCEPTABLE_VALUES: 8,
  ATTRITION_PROBABILITY_TIME_HORIZON_REQUIRES_ATTRITION_PROBABILITY: 9,
  CHART_TYPE_REQUIRES_SPECIFIC_MEASURE: 10,
  CHART_TYPE_REQUIRES_SPECIFIC_DIMENSION: 11,
  COMPARE_PERIOD_REQUIRES_DATE_FILTER: 12,
  COMPARE_PERIOD_DISALLOWS_TIME_DIMENSION: 13,
  COMPARE_PERIOD_PREVIOUS_PERIOD_REQUIRES_SPECIFIC_OPERATORS: 14,
  COMPARE_PERIOD_FILTER_DOES_NOT_SUPPORT_COMPARISON: 15,
  COMPARE_PERIOD_CUSTOM_RANGE_REQUIRES_START_AND_END: 16,
  COMPARE_PERIOD_REQUIRES_BAR_CHART: 17,
  DIMENSIONS_ARE_UNIQUE: 18,
  INSIGHTS_WHEEL_CHART_ONLY_ALLOWS_HEADCOUNT: 19,
  INSIGHTS_WHEEL_CHART_ONLY_ALLOWS_ONE_DIMENSION: 20,
  INSIGHTS_WHEEL_CHART_REQUIRES_A_WHEEL_POSITION_DIMENSION: 21,
  INSIGHTS_WHEEL_CHART_REQUIRES_SHOW_COUNT: 22,
  LINE_CHART_REQUIRES_TIME_DIMENSION: 23,
  MEASURE_DOES_NOT_SUPPORT_DIMENSIONS: 24,
  MEASURE_DOES_NOT_SUPPORT_PROJECTIONS: 25,
  MEASURES_ARE_UNIQUE: 26,
  MEASURE_NOT_IMPLEMENTED: 27,
  MEASURE_REQUIRES_SHOW_COUNT: 28,
  MEASURE_REQUIRES_TIME_DIMENSION_OR_FILTER: 29,
  MULTIPLE_AXIS_GROUPS_REQUIRE_LINE_OR_BAR_CHART: 30,
  ONLY_FOUR_DIMENSIONS_ALLOWED: 31,
  ONLY_ONE_TIME_DIMENSION_ALLOWED: 32,
  ONLY_TWO_AXIS_GROUPS_ALLOWED: 33,
  PIE_CHART_SUPPORTS_ONLY_ONE_DIMENSION: 34,
  PIE_CHART_DOES_NOT_SUPPORT_TIME_DIMENSION: 35,
  RECRUITMENT_STAGE_HISTORICAL_REQUIRES_TIME_IN_STAGE_OR_APPLICANT_COUNT: 36,
  SURVEY_SCORE_REQUIRES_ANSWER_VALUE_DIMENSION: 37,
  TERMINATION_TYPE_AND_TERMINATION_REASON_CANNOT_COEXIST: 38,
  TIME_IN_STAGE_REQUIRES_RECRUITMENT_STAGE_HISTORICAL: 39,
  TIME_TO_FILL_DRIVERS_FORCES_SPECIAL_CONFIGURATION: 40,
  Y_AXIS_OVERRIDE_2_IS_CLEAR_IF_ONLY_ONE_AXIS_GROUP: 41,
} as const;

export type ReportConfigErrorCode =
  (typeof REPORT_CONFIG_ERROR_CODES)[keyof typeof REPORT_CONFIG_ERROR_CODES];

type ReportConfigValidationRule = (
  config: WritableDraft<ReportConfigV2>,
  repair: boolean,
) => ReportConfigValidationRuleResult;

type ReportConfigValidationRuleResult = ReportConfigValidationError | undefined;

type ReportConfigValidationError = {
  code: ReportConfigErrorCode;
  message?: string;
  offender?: MeasureName | DimensionName | FilterName | string;
};

const answerValueDimensionMustComeFirstRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (
    config.data.dimensions.includes("ANSWER_VALUE") &&
    config.data.dimensions[0] !== "ANSWER_VALUE"
  ) {
    if (repair) {
      config.data.dimensions = [
        "ANSWER_VALUE",
        ...config.data.dimensions.filter((dim) => dim !== "ANSWER_VALUE"),
      ];
    }
    const offendingDim = config.data.dimensions.find(
      (dim) => dim !== "ANSWER_VALUE",
    );
    return {
      code: REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_MUST_COME_FIRST,
      offender: offendingDim,
    };
  }
};

const answerValueDimensionOnlyValidWithOneOtherDimension: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.dimensions.includes("ANSWER_VALUE") &&
      config.data.dimensions.filter((dim) => dim !== "ANSWER_VALUE").length > 1
    ) {
      if (repair) {
        // Keep only the first non-ANSWER_VALUE dimension
        const firstOtherDimension = config.data.dimensions.find(
          (dim) => dim !== "ANSWER_VALUE",
        );
        config.data.dimensions = ["ANSWER_VALUE"];
        if (firstOtherDimension) {
          config.data.dimensions.push(firstOtherDimension);
        }
      }
      const otherDims = config.data.dimensions.filter(
          (dim) => dim !== "ANSWER_VALUE",
        ),
        offendingDim = otherDims[otherDims.length - 1];
      return {
        code: REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_ONLY_VALID_WITH_ONE_OTHER_DIMENSION,
        offender: offendingDim,
      };
    }
  };

const avgSurveyScoreNotAllowedWithSurveyScoreRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.measures.includes("AVG_SURVEY_SCORE") &&
      config.data.measures.includes("SURVEY_SCORE")
    ) {
      const avgIndex = config.data.measures.indexOf("AVG_SURVEY_SCORE");
      const surveyIndex = config.data.measures.indexOf("SURVEY_SCORE");
      if (repair) {
        // If both AVG_SURVEY_SCORE and SURVEY_SCORE are present, remove the one appearing last in the array

        if (avgIndex > surveyIndex) {
          config.data.measures.splice(avgIndex, 1);
        } else {
          config.data.measures.splice(surveyIndex, 1);
        }
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.AVG_SURVEY_SCORE_NOT_ALLOWED_WITH_SURVEY_SCORE,
        offender: avgIndex > surveyIndex ? "AVG_SURVEY_SCORE" : "SURVEY_SCORE",
      };
    }
  };

const answerValueDimensionNotAllowedForAvgSurveyScoreRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.measures.includes("AVG_SURVEY_SCORE") &&
      config.data.dimensions.includes("ANSWER_VALUE")
    ) {
      if (repair) {
        config.data.dimensions = config.data.dimensions.filter(
          (dim) => dim !== "ANSWER_VALUE",
        );
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.ANSWER_VALUE_DIMENSION_NOT_ALLOWED_FOR_AVG_SURVEY_SCORE,
        offender: "ANSWER_VALUE",
      };
    }
  };

const surveyScoreRequiresAnswerValueDimensionRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.measures.includes("SURVEY_SCORE") &&
      !config.data.dimensions.includes("ANSWER_VALUE")
    ) {
      if (repair) {
        config.data.dimensions.push("ANSWER_VALUE");
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.SURVEY_SCORE_REQUIRES_ANSWER_VALUE_DIMENSION,
      };
    }
  };

const lineChartRequiresTimeDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const timeDimension = findTimeDimensionV2(config.data);
  if (config.chart.chartType === "Line" && !timeDimension) {
    if (repair) {
      // Add a default time dimension
      addTimeDimensionByDataset(config);
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.LINE_CHART_REQUIRES_TIME_DIMENSION,
    };
  }
};

const measuresAreUniqueRule: ReportConfigValidationRule = (config, repair) => {
  const uniqueMeasures = new Set(config.data.measures);
  if (uniqueMeasures.size < config.data.measures.length) {
    if (repair) {
      config.data.measures = Array.from(uniqueMeasures);
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.MEASURES_ARE_UNIQUE,
    };
  }
};

const dimensionsAreUniqueRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const uniqueDimensions = new Set(config.data.dimensions);
  if (uniqueDimensions.size < config.data.dimensions.length) {
    if (repair) {
      config.data.dimensions = Array.from(uniqueDimensions);
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.DIMENSIONS_ARE_UNIQUE,
    };
  }
};

const multipleAxisGroupsRequireLineOrBarChartRule: ReportConfigValidationRule =
  (config, repair) => {
    const chartTypesSupportingMultipleAxisGroups: ChartTypeName[] = [
      "Line",
      "Bar",
      "Stacked Bar",
    ];
    const distinctAxisGroups = new Set(
      config.data.measures.map((m: MeasureName) => MEASURES[m].axisGroup),
    );
    if (
      distinctAxisGroups.size > 1 &&
      !chartTypesSupportingMultipleAxisGroups.includes(config.chart.chartType)
    ) {
      if (repair) {
        config.chart.chartType = "Bar";
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.MULTIPLE_AXIS_GROUPS_REQUIRE_LINE_OR_BAR_CHART,
      };
    }
  };

const measureDoesNotSupportDimensionsRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const measuresNotSupportingDimensions = config.data.measures.filter(
    (m) => MEASURES[m].disableDimensions,
  );
  if (
    measuresNotSupportingDimensions.length > 0 &&
    config.data.dimensions.length > 0
  ) {
    if (repair) {
      config.data.dimensions = [];
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.MEASURE_DOES_NOT_SUPPORT_DIMENSIONS,
      offender: measuresNotSupportingDimensions[0],
    };
  }
};

const measureDoesNotSupportProjectionsRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const measuresNotSupportingProjections = config.data.measures.filter(
    (m) => MEASURES[m].disableProjections,
  );
  if (
    measuresNotSupportingProjections.length > 0 &&
    config.data.includeProjections
  ) {
    if (repair) {
      config.data.includeProjections = false;
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.MEASURE_DOES_NOT_SUPPORT_PROJECTIONS,
      offender: measuresNotSupportingProjections[0],
    };
  }
};

const chartTypeRequiresSpecificMeasureRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const newChartConfiguration =
    CHART_TYPES_BY_LABEL[config.chart.chartType as ChartTypeName];
  if (newChartConfiguration.requiresMeasure) {
    if (!config.data.measures.includes(newChartConfiguration.requiresMeasure)) {
      if (repair) {
        config.data.measures = [newChartConfiguration.requiresMeasure];
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.CHART_TYPE_REQUIRES_SPECIFIC_MEASURE,
        offender: newChartConfiguration.requiresMeasure,
      };
    }
  }
};

const chartTypeRequiresSpecificDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const newChartConfiguration =
    CHART_TYPES_BY_LABEL[config.chart.chartType as ChartTypeName];
  if (
    newChartConfiguration.requiresDimension &&
    newChartConfiguration.requiresDimension.length > 0
  ) {
    // Check if at least one of the required dimensions is present
    const hasRequiredDimension = newChartConfiguration.requiresDimension.some(
      (dim) => config.data.dimensions.includes(dim),
    );
    if (!hasRequiredDimension) {
      if (repair) {
        // Use the first required dimension as the default
        config.data.dimensions = [newChartConfiguration.requiresDimension[0]];
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.CHART_TYPE_REQUIRES_SPECIFIC_DIMENSION,
        offender: newChartConfiguration.requiresDimension[0],
      };
    }
  }
};

const YAxisOverride2IsClearIfOnlyOneAxisGroupRule: ReportConfigValidationRule =
  (config, repair) => {
    const distinctAxisGroups = new Set(
      config.data.measures.map((m: MeasureName) => MEASURES[m].axisGroup),
    );
    if (distinctAxisGroups.size === 1) {
      if (
        config.chart.yAxisLabelOverride2 &&
        config.chart.yAxisLabelOverride2.length > 0
      ) {
        if (repair) {
          config.chart.yAxisLabelOverride2 = "";
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.Y_AXIS_OVERRIDE_2_IS_CLEAR_IF_ONLY_ONE_AXIS_GROUP,
        };
      }
    }
  };

const measureNotImplementedRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const notImplementedMeasures = config.data.measures.filter(
    (m) => MEASURES[m].columnsReferenced[0] === "NOT_IMPLEMENTED",
  );
  if (notImplementedMeasures.length > 0) {
    if (repair) {
      config.data.measures = config.data.measures.filter(
        (m) => MEASURES[m].columnsReferenced[0] !== "NOT_IMPLEMENTED",
      );
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.MEASURE_NOT_IMPLEMENTED,
      offender: notImplementedMeasures[0],
    };
  }
};

// This rule handles both measures that require a time dimension or filter
// If a measure.requiresTimeDimensionOrFilter is true, and neither are present,
// we add a filter to satisfy the requirement.
const measureRequiresFilterOrTimeDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (
    config.data.measures.some((m) => MEASURES[m].requiresTimeDimensionOrFilter)
  ) {
    const timeDimension = findTimeDimensionV2(config.data);
    if (!timeDimension) {
      const timeFilter = findTimeFilterV2(config.data);

      if (!timeFilter) {
        if (repair) {
          // Add a default time dimension
          addTimeFilterByDataset(config);
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.MEASURE_REQUIRES_TIME_DIMENSION_OR_FILTER,
        };
      }
    }
  }
};

const measureRequiresShowCountRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const measuresRequiringShowCount = config.data.measures.filter(
    (m) => MEASURES[m].alwaysShowCount,
  );
  if (measuresRequiringShowCount.length > 0 && !config.chart.showCount) {
    if (repair) {
      config.chart.showCount = true;
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.MEASURE_REQUIRES_SHOW_COUNT,
      offender: measuresRequiringShowCount[0],
    };
  }
};

const attritionDriversDoesNotSupportDateDimensionRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.datasetName === "attrition_drivers" &&
      config.data.dimensions.includes("DATE")
    ) {
      if (repair) {
        config.data.dimensions = config.data.dimensions.filter(
          (d) => d !== "DATE",
        );
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.ATTRITION_DRIVERS_DOES_NOT_SUPPORT_DATE_DIMENSION,
      };
    }
  };

const attritionTopDriversForcesSpecialConfigurationRule: ReportConfigValidationRule =
  (config, repair) => {
    if (config.data.measures.includes("ATTRITION_DRIVERS")) {
      // Guard: ATTRITION_DRIVERS can only be used with its preset"
      if (repair) {
        const newConfig = getInitialReportConfig("attrition_drivers");
        Object.assign(config, newConfig);
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.ATTRITION_DRIVERS_FORCES_SPECIAL_CONFIGURATION,
      };
    }
  };

const timeToFillDriversForcesSpecialConfigurationRule: ReportConfigValidationRule =
  (config, repair) => {
    if (config.data.measures.includes("TIME_TO_FILL_DRIVERS")) {
      // Guard: TIME_TO_FILL_DRIVERS can only be used with its preset"
      if (repair) {
        const newConfig = getInitialReportConfig("time_to_fill_drivers");
        Object.assign(config, newConfig);
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.TIME_TO_FILL_DRIVERS_FORCES_SPECIAL_CONFIGURATION,
      };
    }
  };

const onlyOneTimeDimensionAllowedRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const timeDimensions = config.data.dimensions.filter(
    (dimName: DimensionName) => DIMENSIONS[dimName].isTimeDimension,
  );
  if (timeDimensions.length > 1) {
    const lastTimeDimension = timeDimensions[timeDimensions.length - 1];
    if (repair) {
      // Keep only the last time dimension
      config.data.dimensions = config.data.dimensions.filter(
        (dimName: DimensionName) =>
          !DIMENSIONS[dimName].isTimeDimension || dimName === lastTimeDimension,
      );
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.ONLY_ONE_TIME_DIMENSION_ALLOWED,
      offender: lastTimeDimension,
    };
  }
};

const onlyTwoAxisGroupsAllowedRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const distinctAxisGroups = new Set(
    config.data.measures.map((m: MeasureName) => MEASURES[m].axisGroup),
  );
  if (distinctAxisGroups.size > 2) {
    if (repair) {
      // Keep only measures from the first two axis groups
      const allowedAxisGroups = Array.from(distinctAxisGroups).slice(0, 2);
      config.data.measures = config.data.measures.filter((m: MeasureName) =>
        allowedAxisGroups.includes(MEASURES[m].axisGroup),
      );
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.ONLY_TWO_AXIS_GROUPS_ALLOWED,
    };
  }
};

const onlyFourDimensionsAllowedRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.data.dimensions.length > 4) {
    if (repair) {
      config.data.dimensions = config.data.dimensions.slice(0, 4);
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.ONLY_FOUR_DIMENSIONS_ALLOWED,
    };
  }
};

const recruitmentStageHistoricalRequiresTimeInStageOrApplicantCountRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.dimensions.includes("RECRUITMENT_STAGE_HISTORICAL") &&
      !config.data.measures.includes("TIME_IN_STAGE") &&
      !config.data.measures.includes("APPLICANT_COUNT")
    ) {
      if (repair) {
        config.data.measures.push("TIME_IN_STAGE");
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.RECRUITMENT_STAGE_HISTORICAL_REQUIRES_TIME_IN_STAGE_OR_APPLICANT_COUNT,
      };
    }
  };

const terminationTypeAndTerminationReasonCannotCoexistRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.dimensions.includes("TERMINATION_TYPE") &&
      config.data.dimensions.includes("TERMINATION_REASON")
    ) {
      if (repair) {
        config.data.dimensions = config.data.dimensions.filter(
          (dimName: DimensionName) => dimName !== "TERMINATION_REASON",
        );
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.TERMINATION_TYPE_AND_TERMINATION_REASON_CANNOT_COEXIST,
      };
    }
  };

const timeInStageRequiresRecruitmentStageHistoricalRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.measures.includes("TIME_IN_STAGE") &&
      !config.data.dimensions.includes("RECRUITMENT_STAGE_HISTORICAL")
    ) {
      if (repair) {
        config.data.dimensions.push("RECRUITMENT_STAGE_HISTORICAL");
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.TIME_IN_STAGE_REQUIRES_RECRUITMENT_STAGE_HISTORICAL,
      };
    }
  };

const pieChartSupportsOnlyOneDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.chart.chartType === "Pie" && config.data.dimensions.length > 1) {
    if (repair) {
      // Keep only the first dimension
      config.data.dimensions = config.data.dimensions.slice(0, 1);
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.PIE_CHART_SUPPORTS_ONLY_ONE_DIMENSION,
    };
  }
};

const pieChartDoesNotSupportTimeDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (
    config.chart.chartType === "Pie" &&
    config.data.dimensions.some((dim) => DIMENSIONS[dim].isTimeDimension)
  ) {
    if (repair) {
      // Switch to line chart if there's a time dimension, since pie charts don't support time dimensions
      config.chart.chartType = "Line";
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.PIE_CHART_DOES_NOT_SUPPORT_TIME_DIMENSION,
    };
  }
};

const attritionProbabilityRequiresAttritionTimeHorizonFilterRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.measures.includes("ATTRITION_PROBABILITY") &&
      !config.data.filters.some(
        (filter) =>
          filter.filterDefinitionName === "ATTRITION_PROBABILITY_TIME_HORIZON",
      )
    ) {
      if (repair) {
        config.data.filters.push({
          filterDefinitionName: "ATTRITION_PROBABILITY_TIME_HORIZON",
          operator: "EQUALS",
          value: "attrition_6m",
        });
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.ATTRITION_PROBABILITY_REQUIRES_ATTRITION_TIME_HORIZON_FILTER,
      };
    }
  };

const attritionProbabilityTimeHorizonRequiresAttritionProbabilityRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.filters.some(
        (filter) =>
          filter.filterDefinitionName === "ATTRITION_PROBABILITY_TIME_HORIZON",
      ) &&
      !config.data.measures.includes("ATTRITION_PROBABILITY")
    ) {
      if (repair) {
        config.data.filters = config.data.filters.filter(
          (filter) =>
            filter.filterDefinitionName !==
            "ATTRITION_PROBABILITY_TIME_HORIZON",
        );
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.ATTRITION_PROBABILITY_TIME_HORIZON_REQUIRES_ATTRITION_PROBABILITY,
      };
    }
  };

const attritionProbabilityTimeHorizonAcceptableValuesRule: ReportConfigValidationRule =
  (config, repair) => {
    const timeHorizonFilter = config.data.filters.find(
      (filter) =>
        filter.filterDefinitionName === "ATTRITION_PROBABILITY_TIME_HORIZON",
    );
    if (timeHorizonFilter) {
      const acceptableValues = [
        "attrition_3m",
        "attrition_6m",
        "attrition_12m",
        "attrition_18m",
      ];
      if (!acceptableValues.includes(timeHorizonFilter.value as string)) {
        if (repair) {
          // search for 3, 6, 12, or 18 in the value and set accordingly, default to 6 months if no match
          const match = /(\d+)\s*months?/.exec(
            timeHorizonFilter.value as string,
          );
          let months = match ? parseInt(match[1], 10) : 6;
          if (![3, 6, 12, 18].includes(months)) {
            months = 6;
          }
          timeHorizonFilter.value = `attrition_${months}m`;
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.ATTRITION_PROBABILITY_TIME_HORIZON_ACCEPTABLE_VALUES,
          offender: timeHorizonFilter.value as string,
        };
      }
    }
  };

const comparePeriodRequiresDateFilterRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.data.comparisonEnabled && !findTimeFilterV2(config.data)) {
    if (repair) {
      // remove comparison period filter if no date filter is present
      config.data.comparisonEnabled = false;
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_REQUIRES_DATE_FILTER,
    };
  }
};

const comparePeriodDisallowsTimeDimensionRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.data.comparisonEnabled) {
    const foundTimeDimension = findTimeDimensionV2(config.data);
    if (foundTimeDimension) {
      if (repair) {
        // remove the time dimension from the config if comparison is enabled
        config.data.dimensions = config.data.dimensions.filter(
          (dim) => dim !== foundTimeDimension.dimension,
        );
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_DISALLOWS_TIME_DIMENSION,
      };
    }
  }
};

const comparePeriodPreviousPeriodRequiresSpecificOperatorsRule: ReportConfigValidationRule =
  (config, repair) => {
    if (
      config.data.comparisonEnabled &&
      ["previousPeriod", "oneYearAgo"].includes(config.data.compareWith)
    ) {
      const previousPeriodFilter = findTimeFilterV2(config.data);
      if (previousPeriodFilter) {
        const operator = previousPeriodFilter.operator,
          validOperators = [
            "BETWEEN",
            "LAST_30_DAYS",
            "LAST_90_DAYS",
            "LAST_180_DAYS",
            "LAST_365_DAYS",
          ];
        if (!validOperators.includes(operator)) {
          if (repair) {
            // Convert to an equivalent bounded range so BETWEEN always has both endpoints
            const date = toPlainDate(previousPeriodFilter.value);
            const today = todayUTC();
            if (
              operator === "AFTER" &&
              date &&
              Temporal.PlainDate.compare(date, today) < 0
            ) {
              previousPeriodFilter.operator = "BETWEEN";
              previousPeriodFilter.value = toISODate(date);
              previousPeriodFilter.secondaryValue = toISODate(today);
            } else if (operator === "BEFORE" && date) {
              previousPeriodFilter.operator = "BETWEEN";
              previousPeriodFilter.value = toISODate(
                date.subtract({ years: 1 }),
              );
              previousPeriodFilter.secondaryValue = toISODate(date);
            } else {
              previousPeriodFilter.operator = "LAST_365_DAYS";
              previousPeriodFilter.value = toISODate(
                today.subtract({ days: 365 }),
              );
              previousPeriodFilter.secondaryValue = undefined;
            }
          }
          return {
            code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_PREVIOUS_PERIOD_REQUIRES_SPECIFIC_OPERATORS,
            offender: operator,
          };
        }
      }
    }
  };

const comparePeriodCustomRangeRequiresStartAndEndRule: ReportConfigValidationRule =
  (config, repair) => {
    if (config.data.comparisonEnabled && config.data.compareWith === "custom") {
      if (
        parsePlainDate(config.data.compareCustomPeriodStart as string) &&
        parsePlainDate(config.data.compareCustomPeriodEnd as string)
      ) {
        // valid custom range
      } else {
        if (repair) {
          config.data.comparisonEnabled = false;
          config.data.compareCustomPeriodStart = undefined;
          config.data.compareCustomPeriodEnd = undefined;
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_CUSTOM_RANGE_REQUIRES_START_AND_END,
        };
      }
    }
  };

const comparePeriodFilterSupportsComparisonRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.data.comparisonEnabled) {
    const foundTimeFilter = findTimeFilterV2(config.data);
    if (
      foundTimeFilter &&
      !(FILTERS[foundTimeFilter.filterDefinitionName] as DateFilterDefinition)
        .supportsSinglePeriodView
    ) {
      if (repair) {
        config.data.comparisonEnabled = false;
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_FILTER_DOES_NOT_SUPPORT_COMPARISON,
      };
    }
  }
};

const comparePeriodRequiresBarChartRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  const validChartTypes: ChartTypeName[] = ["Bar", "Horizontal Bar"];
  if (
    config.data.comparisonEnabled &&
    !validChartTypes.includes(config.chart.chartType)
  ) {
    if (repair) {
      config.chart.chartType = "Bar";
    }
    return {
      code: REPORT_CONFIG_ERROR_CODES.COMPARE_PERIOD_REQUIRES_BAR_CHART,
    };
  }
};

const insightsWheelChartOnlyAllowsHeadcountRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.chart.chartType === "Insights Wheel") {
    const allMeasures = config.data.measures.length;
    const headcountMeasures = config.data.measures.filter(
      (measure) => measure === "HEADCOUNT",
    );
    if (headcountMeasures.length === 0) {
      if (repair) {
        config.data.measures = ["HEADCOUNT"];
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.INSIGHTS_WHEEL_CHART_ONLY_ALLOWS_HEADCOUNT,
      };
    }
    if (allMeasures > 1) {
      if (repair) {
        config.data.measures = ["HEADCOUNT"];
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.INSIGHTS_WHEEL_CHART_ONLY_ALLOWS_HEADCOUNT,
      };
    }
  }
};

const insightsWheelChartOnlyAllowsOneDimensionRule: ReportConfigValidationRule =
  (config, repair) => {
    if (config.chart.chartType === "Insights Wheel") {
      const dimensions = config.data.dimensions.length;
      if (dimensions > 1) {
        if (repair) {
          config.data.dimensions = [config.data.dimensions[0]];
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.INSIGHTS_WHEEL_CHART_ONLY_ALLOWS_ONE_DIMENSION,
        };
      }
    }
  };

const insightsWheelChartRequiresAWheelPositionDimensionRule: ReportConfigValidationRule =
  (config, repair) => {
    if (config.chart.chartType === "Insights Wheel") {
      const hasWheelPositionDimension =
        config.data.dimensions.includes("INSIGHTS_72_TYPE") ||
        config.data.dimensions.includes("INSIGHTS_LESS_CONSCIOUS_72_TYPE");
      if (!hasWheelPositionDimension) {
        if (repair) {
          config.data.dimensions = ["INSIGHTS_72_TYPE"];
        }
        return {
          code: REPORT_CONFIG_ERROR_CODES.INSIGHTS_WHEEL_CHART_REQUIRES_A_WHEEL_POSITION_DIMENSION,
        };
      }
    }
  };

const insightsWheelChartRequiresShowCountRule: ReportConfigValidationRule = (
  config,
  repair,
) => {
  if (config.chart.chartType === "Insights Wheel") {
    const showCount = config.chart.showCount;
    if (!showCount) {
      if (repair) {
        config.chart.showCount = true;
      }
      return {
        code: REPORT_CONFIG_ERROR_CODES.INSIGHTS_WHEEL_CHART_REQUIRES_SHOW_COUNT,
      };
    }
  }
};

const VALIDATION_RULES = [
  measuresAreUniqueRule,
  dimensionsAreUniqueRule,
  insightsWheelChartOnlyAllowsHeadcountRule,
  insightsWheelChartOnlyAllowsOneDimensionRule,
  insightsWheelChartRequiresAWheelPositionDimensionRule,
  insightsWheelChartRequiresShowCountRule,
  lineChartRequiresTimeDimensionRule,
  multipleAxisGroupsRequireLineOrBarChartRule,
  chartTypeRequiresSpecificMeasureRule,
  chartTypeRequiresSpecificDimensionRule,
  measureRequiresFilterOrTimeDimensionRule,
  attritionDriversDoesNotSupportDateDimensionRule,
  onlyOneTimeDimensionAllowedRule,
  onlyFourDimensionsAllowedRule,
  terminationTypeAndTerminationReasonCannotCoexistRule,
  pieChartSupportsOnlyOneDimensionRule,
  pieChartDoesNotSupportTimeDimensionRule,
  measureRequiresShowCountRule,
  YAxisOverride2IsClearIfOnlyOneAxisGroupRule,
  measureNotImplementedRule,
  attritionTopDriversForcesSpecialConfigurationRule,
  timeToFillDriversForcesSpecialConfigurationRule,
  onlyTwoAxisGroupsAllowedRule,
  measureDoesNotSupportDimensionsRule,
  measureDoesNotSupportProjectionsRule,
  timeInStageRequiresRecruitmentStageHistoricalRule,
  recruitmentStageHistoricalRequiresTimeInStageOrApplicantCountRule,
  attritionProbabilityRequiresAttritionTimeHorizonFilterRule,
  attritionProbabilityTimeHorizonRequiresAttritionProbabilityRule,
  attritionProbabilityTimeHorizonAcceptableValuesRule,
  avgSurveyScoreNotAllowedWithSurveyScoreRule,
  surveyScoreRequiresAnswerValueDimensionRule,
  answerValueDimensionNotAllowedForAvgSurveyScoreRule,
  answerValueDimensionMustComeFirstRule,
  answerValueDimensionOnlyValidWithOneOtherDimension,
  comparePeriodRequiresDateFilterRule,
  comparePeriodDisallowsTimeDimensionRule,
  comparePeriodPreviousPeriodRequiresSpecificOperatorsRule,
  comparePeriodCustomRangeRequiresStartAndEndRule,
  comparePeriodFilterSupportsComparisonRule,
  comparePeriodRequiresBarChartRule,
];

type ValidateReportConfigOptions = {
  config: ReportConfigV2;
  repair?: boolean;
  depth?: number;
};

export const validateReportConfigV2 = ({
  config,
  repair = false,
  depth = 0,
}: ValidateReportConfigOptions): {
  valid: boolean;
  errors: ReportConfigValidationError[];
  errorCodes: ReportConfigErrorCode[];
  repairedReportConfig?: ReportConfigV2;
} => {
  let valid = true;
  const errors: ReportConfigValidationError[] = [];

  for (const rule of VALIDATION_RULES) {
    const result = rule(config, repair);
    if (result) {
      valid = false;
      errors.push(result);
    }
  }

  if (repair && !valid && depth < 2) {
    return validateReportConfigV2({
      config,
      repair,
      depth: depth + 1,
    });
  }

  return {
    valid,
    errors,
    errorCodes: errors.map((e) => e.code),
    repairedReportConfig: repair ? config : undefined,
  };
};
